/**
 * **One picture window** — C24 item 9, [ADR-0047](../../../../../docs/adr/0047-one-picture-window-and-pictures-the-product-ships.md) §2, T-4.124.
 *
 * The owner, 2026-10-01:
 *
 * > *at any place wehere we have option to upload image or select or use
 * > default. chose from pictures and use default should be in single window.
 * > all picture on single button and single place*
 *
 * Before this there were four ways to set a picture and they did not look like
 * each other: `ImageField` drew *Upload*, *Choose from library* and a URL box
 * as three controls side by side; `LogoControl` drew its own flow with a
 * fourth thing nothing else had — the shipped defaults. One button now opens
 * one window, and the window has the places a picture can come from as tabs.
 *
 * ## The tabs
 *
 * | Tab | What it is |
 * |---|---|
 * | **Your pictures** | the firm's library (ADR-0021), searchable, **with upload and drop inside it** |
 * | **Graphics we ship** | C24 item 3's gallery (ADR-0047 §1), grouped and searchable |
 * | **Logos we ship** | **in place of** the graphics, when `logos` is given — ADR-0030's four, the firm's profession first |
 * | **Link** | paste an address, as before |
 *
 * **In place of, not beside.** The shipped graphics are backdrops, charts and
 * textures; none of them is a logo, and offering a firm a contour pattern as
 * its mark would be a worse screen than offering it nothing. So the logo
 * configuration swaps that tab rather than adding a fourth.
 *
 * **Upload is not a tab**, and that is deliberate (ADR-0047 §2): it is an
 * action rather than a place to browse, and it already lives inside *Your
 * pictures*, which is where an uploaded picture lands. `MediaBrowser`'s pick
 * mode says so in its own code — *one upload **is** the choice* — so a fourth
 * tab would be a second button for one code path, and a firm that uploaded
 * from it would watch the window jump to a tab it had not chosen.
 *
 * ## It lands on a tab with something in it
 *
 * A new firm has an empty library, and opening a picker on an empty grid with
 * an upload button is a worse first screen than opening on the gallery that
 * already has twenty-two drawings in it. So `initialTab` is *Your pictures*
 * when the firm has any and the shipped gallery when it has none — asked once,
 * when the window opens, never re-decided while it is open.
 *
 * ## Dimensions travel with the picture
 *
 * Every picked picture carries `width` and `height` where they are **known**:
 * the library measured its own on the server (ADR-0021), and a shipped
 * graphic's are in the manifest and held to the drawing's `viewBox` by the
 * build. They are `null` only for a pasted link, which is the one case the
 * browser still has to measure. That is what lets a page reserve a picture's
 * box before a byte arrives (the Wave 3 close's §6.1) — and the reason this
 * component returns a shape rather than a string.
 */

import { Alert, Button, Dialog, Input, TabPanel, Tabs, type TabDef } from '@fiveox/ui'
import { Check, ImageOff, Link2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  DEFAULT_LOGO_PREFIX,
  defaultLogosFor,
  hasOwnDefault,
  type DefaultLogo,
} from '../../lib/defaultLogos'
import { siteMedia, type SiteMediaItem } from '../../lib/api/siteMedia'
import { STOCK_PICTURES, searchStock, stockByGroup, type StockPicture } from '../../lib/stockPictures'
import { MediaBrowser } from '../../routes/Site/fields/MediaLibrary'

/**
 * A picture the firm chose, wherever it came from.
 *
 * `width`/`height` are `null` only for a pasted link — see the file header.
 * `label` is a suggested alternative text (a shipped graphic's words, a shipped
 * logo's name); a caller that already has one of its own ignores it.
 */
export interface PickedPicture {
  url: string
  width: number | null
  height: number | null
  label?: string
  /** Where it came from, for a caller that words its own line under the preview. */
  source: 'library' | 'stock' | 'logo' | 'link'
}

export type PictureTab = 'library' | 'stock' | 'logos' | 'link'

export interface PicturePickerProps {
  open: boolean
  onClose: () => void
  onPick: (picture: PickedPicture) => void
  /** `site:can_update`. Without it the library is read-only (FR-I13). */
  editable: boolean
  /**
   * Offer the shipped **logos** as a tab, with this profession's first
   * (ADR-0030 §3). Given only by `LogoControl`; a logo is not an ordinary
   * picture and no other field offers them.
   */
  logos?: { profession: string | null } | null
  /** The address already in the field, so the Link tab opens on it. */
  value?: string | null
  /** Overrides the landing tab. Used by tests and by a caller that knows. */
  initialTab?: PictureTab
  title?: string
}

/** The address schemes a pasted link may use. Nothing else is accepted. */
export function linkProblem(raw: string): string | null {
  const value = raw.trim()
  if (!value) return 'Paste the address of a picture.'
  // A root-relative path is how the firm's own library and our shipped
  // graphics are addressed, so it has to be allowed — it is also the only
  // relative form that means the same thing on every page of the site.
  if (value.startsWith('/')) return null
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return 'That does not look like a web address. It should start with https://'
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return 'A picture’s address has to start with https://'
  }
  return null
}

export function PicturePicker({
  open,
  onClose,
  onPick,
  editable,
  logos = null,
  value = null,
  initialTab,
  title = 'Choose a picture',
}: PicturePickerProps) {
  const tabs = useMemo<TabDef[]>(() => {
    const list: TabDef[] = [{ id: 'library', label: 'Your pictures' }]
    // One or the other, never both — see the file header.
    if (logos) list.push({ id: 'logos', label: 'Logos we ship' })
    else list.push({ id: 'stock', label: 'Graphics we ship', badge: String(STOCK_PICTURES.length) })
    list.push({ id: 'link', label: 'Link' })
    return list
  }, [logos])

  const [active, setActive] = useState<string>(initialTab ?? 'library')
  /** Whether the firm has any picture of its own. `null` while unasked. */
  const [hasOwn, setHasOwn] = useState<boolean | null>(null)

  // Asked once per opening. A read that fails is read as *no pictures*, which
  // lands the firm on the gallery — a worse guess than the truth and a better
  // screen than an error on a window whose job is to offer a choice.
  useEffect(() => {
    if (!open || initialTab) return
    let cancelled = false
    setHasOwn(null)
    void siteMedia
      .list({ q: '', offset: 0, limit: 1 })
      .then((body) => !cancelled && setHasOwn(body.data.length > 0))
      .catch(() => !cancelled && setHasOwn(false))
    return () => {
      cancelled = true
    }
  }, [open, initialTab])

  // The landing tab, applied when the answer arrives and never again: a firm
  // that has navigated to another tab is not moved out of it.
  const [landed, setLanded] = useState(false)
  useEffect(() => {
    if (!open) {
      setLanded(false)
      return
    }
    if (landed || initialTab || hasOwn === null) return
    // A firm with pictures of its own starts there; a firm with none starts on
    // whatever the product itself offers, rather than on an empty grid.
    setActive(hasOwn ? 'library' : logos ? 'logos' : 'stock')
    setLanded(true)
  }, [open, landed, initialTab, hasOwn, logos])

  const take = (picture: PickedPicture) => {
    onPick(picture)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} title={title} width="xl">
      {open ? (
        <div className="space-y-4">
          <Tabs tabs={tabs} active={active} onChange={setActive} idBase="picture-picker" />

          <TabPanel id="library" active={active} idBase="picture-picker">
            <MediaBrowser
              mode="pick"
              editable={editable}
              onPick={(item: SiteMediaItem) =>
                take({
                  url: item.url,
                  width: item.width,
                  height: item.height,
                  label: item.alt || undefined,
                  source: 'library',
                })
              }
            />
          </TabPanel>

          {logos ? (
            <TabPanel id="logos" active={active} idBase="picture-picker">
              <ShippedLogos profession={logos.profession} onPick={take} />
            </TabPanel>
          ) : (
            <TabPanel id="stock" active={active} idBase="picture-picker">
              <StockGallery onPick={take} />
            </TabPanel>
          )}

          <TabPanel id="link" active={active} idBase="picture-picker">
            <LinkPanel value={value} onPick={take} />
          </TabPanel>
        </div>
      ) : null}
    </Dialog>
  )
}

/* ── Graphics we ship ─────────────────────────────────────────────────────── */

export function StockGallery({ onPick }: { onPick: (picture: PickedPicture) => void }) {
  const [q, setQ] = useState('')
  const searching = q.trim().length > 0
  const matches = useMemo(() => searchStock(q), [q])
  const sections = useMemo(() => stockByGroup(), [])

  const card = (picture: StockPicture) => (
    <button
      key={picture.file}
      type="button"
      data-stock={picture.file}
      title={picture.label}
      onClick={() =>
        onPick({
          url: picture.url,
          width: picture.width,
          height: picture.height,
          label: picture.label,
          source: 'stock',
        })
      }
      className="group overflow-hidden rounded-xl border border-border bg-card text-left transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <img
        src={picture.url}
        alt={picture.label}
        width={picture.width}
        height={picture.height}
        className="aspect-[4/3] w-full bg-muted object-cover"
        loading="lazy"
      />
      <span className="block px-2.5 py-2 text-xs text-muted-foreground">{picture.label}</span>
    </button>
  )

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the graphics…"
          aria-label="Search the graphics we ship"
        />
        {/* Said plainly, so a firm hunting for a photograph of its own office
            is not left to work it out from the pictures. */}
        <p className="text-xs text-muted-foreground">
          Drawings we ship with ProDesk, free for every firm to use. They are not photographs — for
          a picture of your own office, upload it under <strong className="font-medium">Your pictures</strong>.
        </p>
      </div>

      {searching ? (
        matches.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No graphic matches “{q.trim()}”.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{matches.map(card)}</div>
        )
      ) : (
        sections.map((section) => (
          <section key={section.group.key} className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {section.group.label}
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {section.pictures.map(card)}
            </div>
          </section>
        ))
      )}
    </div>
  )
}

/* ── Logos we ship (only for the logo control) ────────────────────────────── */

export function ShippedLogos({
  profession,
  onPick,
}: {
  profession: string | null
  onPick: (picture: PickedPicture) => void
}) {
  // **A profession with no default of its own is told so** — ADR-0030 §3 and
  // ADR-0031 §4. `other` has none (*"for others they will upload their
  // logos"*, the owner, 2026-09-25), and offering a chartered accountant's
  // mark to an architect would be worse than offering nothing.
  if (!hasOwnDefault(profession)) {
    return (
      <div className="max-w-xl space-y-2 py-4">
        <p className="text-sm text-foreground">There is no shipped logo for your profession.</p>
        <p className="text-xs text-muted-foreground">
          Upload your own under <strong className="font-medium">Your pictures</strong> — drop it on
          the grid or use the button there, and it becomes your logo at once.
        </p>
      </div>
    )
  }
  const logos = defaultLogosFor(profession)
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        A mark you can use until you have your own. Your profession’s is first.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {logos.map((logo: DefaultLogo) => (
          <button
            key={logo.file}
            type="button"
            data-shipped-logo={logo.file}
            onClick={() => onPick({ url: logo.url, width: null, height: null, label: logo.name, source: 'logo' })}
            className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-3 transition hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <img src={logo.url} alt={logo.name} className="h-14 w-auto max-w-full object-contain" loading="lazy" />
            <span className="text-center text-xs text-muted-foreground">{logo.name}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        These are served from <code className="text-[0.95em]">{DEFAULT_LOGO_PREFIX}</code> on your own
        address, so nothing on your website is fetched from anywhere else.
      </p>
    </div>
  )
}

/* ── Link ─────────────────────────────────────────────────────────────────── */

export function LinkPanel({
  value,
  onPick,
}: {
  value: string | null
  onPick: (picture: PickedPicture) => void
}) {
  const [typed, setTyped] = useState(value ?? '')
  const [problem, setProblem] = useState<string | null>(null)

  const use = () => {
    const found = linkProblem(typed)
    if (found) {
      setProblem(found)
      return
    }
    // No dimensions: a pasted address is the one case the browser still has
    // to measure, which `ImageField` does and has always done.
    onPick({ url: typed.trim(), width: null, height: null, source: 'link' })
  }

  return (
    <div className="max-w-xl space-y-3">
      <Input
        label="Picture address"
        value={typed}
        onChange={(e) => {
          setProblem(null)
          setTyped(e.target.value)
        }}
        placeholder="https://…"
        inputMode="url"
        spellCheck={false}
        autoCapitalize="none"
        hint="A picture somewhere else on the web. We measure it so your page keeps its space while it loads."
      />
      {problem && <Alert tone="error">{problem}</Alert>}
      {!problem && typed.trim() ? (
        <div className="flex items-center gap-3">
          <img
            src={typed.trim()}
            alt=""
            className="h-16 w-24 rounded border border-border object-cover"
            loading="lazy"
          />
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ImageOff size={12} aria-hidden /> If nothing shows here, the address is wrong or the
            other site will not let us load it.
          </p>
        </div>
      ) : null}
      <Button onClick={use} disabled={!typed.trim()}>
        <Link2 size={14} aria-hidden /> Use this address
      </Button>
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <Check size={12} className="mt-0.5 shrink-0" aria-hidden />
        A picture you upload is served from your own address and keeps working if the other site goes
        away. A link does not.
      </p>
    </div>
  )
}
